import { Component, computed, effect, inject, signal } from '@angular/core';
import { form, FormField, required } from '@angular/forms/signals';
import { AttendancePrescriptionCandidateObject } from '@libs/types/src/services/object-type/attendance/attendance-prescription-candidate.object';
import { CalendarCheckoutTypeEnum } from '@libs/types/src/services/enum/calendar-checkout-type.enum';
import { BaseDialogComponent } from '../../../kit/base-dialog/base-dialog.component';
import { DateScrollerInputComponent } from '../../../kit/input/date-scroller-input-v2/date-scroller-input.component';
import { ActionButtonsComponent } from '../../../kit/action-buttons/action-buttons.component';
import { LoadingComponent } from '../../../kit/loading/loading.component';
import { EmptyStateComponent } from '../../../../modules/shared/components/empty-state/empty-state.component';
import { checkOk } from '../../../../modules/content/content.error';
import { dateToEndOfDay, dateToStartOfDay } from '@libs/shared/utils/date.utils';
import { AttendanceCandidatesDialogStore } from './attendance-candidates-dialog.store';
import { attendanceCandidateFragment } from './attendance-candidates-dialog.query';
import {
    AttendanceCandidateConfirmEvent,
    AttendanceCandidatesDialogData,
} from './attendance-candidates-dialog.types';
import { AttendanceCandidateCardComponent } from '../attendance-candidate-card/attendance-candidate-card.component';

@Component({
    selector: 'app-attendance-candidates-dialog',
    templateUrl: './attendance-candidates-dialog.component.html',
    styleUrl: './attendance-candidates-dialog.component.scss',
    providers: [AttendanceCandidatesDialogStore],
    imports: [
        FormField,
        DateScrollerInputComponent,
        ActionButtonsComponent,
        LoadingComponent,
        EmptyStateComponent,
        AttendanceCandidateCardComponent,
    ],
})
export class AttendanceCandidatesDialogComponent extends BaseDialogComponent {

    protected readonly store = inject(AttendanceCandidatesDialogStore);
    protected readonly data = this.dialogConfig.data as AttendanceCandidatesDialogData;
    protected readonly formData = signal({ date: new Date() as Date | null });
    protected readonly formDef = form(this.formData, schema => required(schema.date));
    protected readonly candidates = computed<AttendancePrescriptionCandidateObject[]>(() =>
        this.store.data.getAttendancePrescriptionCandidates() ?? [],
    );

    constructor() {
        super();
        effect(() => {
            const date = this.formData().date;
            if (date) void this.load(date);
        });
    }

    protected async confirmAttendance(event: AttendanceCandidateConfirmEvent): Promise<void> {
        const currentUserId = this.store.globalGlobalStore.loggedUserStore.currentUserId();
        if (!currentUserId) return;

        const response = await this.store.api.createAttendance({
            date: new Date(event.candidate.date),
            timeFrom: this.timeToMinutes(event.timeFrom),
            timeTo: this.timeToMinutes(event.timeTo),
            note: event.note,
            personId: event.candidate.personId,
            userIds: [currentUserId],
        }, 'id');
        checkOk(response);
        await this.reload();
    }

    protected async checkout(candidate: AttendancePrescriptionCandidateObject): Promise<void> {
        const date = new Date(candidate.date);
        const response = await this.store.api.createCalendarCheckout({
            type: CalendarCheckoutTypeEnum.ALL_DAY,
            personId: candidate.personId,
            dateFrom: dateToStartOfDay(date).toISOString(),
            dateTo: dateToEndOfDay(date).toISOString(),
        }, 'id');
        checkOk(response);
        await this.reload();
    }

    private async reload(): Promise<void> {
        const date = this.formData().date;
        if (date) await this.load(date);
    }

    private async load(date: Date): Promise<void> {
        await this.store.api.getAttendancePrescriptionCandidates({
            date,
            serviceIds: this.data.serviceIds,
        }, attendanceCandidateFragment);
    }

    private timeToMinutes(time: Date): number {
        return time.getHours() * 60 + time.getMinutes();
    }

}
